import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, AlertTriangle, XCircle, Lock, Unlock, Plus, Upload } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, StatusBadge } from "@/components/data/StatusBadge";
import { MotifDialog } from "@/components/data/Dialogs";
import { RecapTable } from "@/components/tfj/RecapTable";
import { EncaissementDialog } from "@/components/tfj/EncaissementDialog";
import { ImportDialog } from "@/components/tfj/ImportDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, fmtMoney, fmtNum } from "@/lib/api";

const ICONS = { ok: [CheckCircle2, "text-emerald-600"], warning: [AlertTriangle, "text-amber-600"], error: [XCircle, "text-primary"] };

function Controles({ ctrl }) {
  if (!ctrl) return null;
  return (
    <div className="bg-card border rounded-lg divide-y" data-testid="controles-list">
      {ctrl.checks.map((c) => {
        const [Icon, cls] = ICONS[c.niveau];
        return <div key={`${c.niveau}-${c.message}`} className="p-3 flex gap-3 text-sm"><Icon className={`h-4 w-4 mt-0.5 shrink-0 ${cls}`} />{c.message}</div>;
      })}
      <div className="p-3 flex flex-wrap gap-2 text-xs">
        {Object.entries(ctrl.etats).map(([k, v]) => <span key={k} className="flex items-center gap-1"><StatusBadge value={k} /> {v} revendeur(s)</span>)}
      </div>
    </div>
  );
}

function Encaissements({ journee, items, canCancel, onCancel }) {
  return (
    <div className="bg-card border rounded-lg overflow-x-auto">
      <table className="w-full text-xs" data-testid="encaissements-table">
        <thead className="bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
          <tr><th className="p-2.5 text-left">N°</th><th className="p-2.5 text-left">Superviseur</th><th className="p-2.5 text-right">Montant</th><th className="p-2.5 text-left">Moyen / compte</th><th className="p-2.5 text-left">Répartition</th><th className="p-2.5 text-left">Statut</th><th /></tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.id} className={`border-t align-top ${e.statut === "annule" ? "opacity-55" : ""}`}>
              <td className="p-2.5 font-mono">{e.numero}<div className="text-muted-foreground font-sans">{fmtDate(e.date)} · {e.created_by_name}</div></td>
              <td className="p-2.5">{e.superviseur_id_label}</td>
              <td className="p-2.5 text-right font-mono font-semibold">{fmtMoney(e.montant)}</td>
              <td className="p-2.5">{e.moyen_paiement_id_label}<div className="text-muted-foreground">{e.compte_id_label}{e.reference ? ` · ${e.reference}` : ""}</div></td>
              <td className="p-2.5">{e.repartition.map((a) => <div key={a.revendeur_id}>{a.revendeur_label} : <span className="font-mono">{fmtNum(a.montant)}</span></div>)}</td>
              <td className="p-2.5"><StatusBadge value={e.statut} />{e.motif_annulation && <div className="text-muted-foreground mt-1">{e.motif_annulation}</div>}</td>
              <td className="p-2.5">{canCancel && e.statut === "valide" && journee.statut !== "cloturee" && <Button size="sm" variant="ghost" onClick={() => onCancel(e)} data-testid={`enc-cancel-${e.id}`}>Annuler</Button>}</td>
            </tr>
          ))}
          {!items.length && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Aucun encaissement enregistré</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Cloture({ journee, ctrl, recapKey, canClose, onClose, onReopen, canReopen }) {
  const [commentaire, setCommentaire] = useState("");
  const [anomalies, setAnomalies] = useState([]);
  useEffect(() => {
    api.get(`/tfj/journees/${journee.id}/recap`, { params: { niveau: "revendeur" } })
      .then((r) => setAnomalies(r.data.rows.filter((x) => x.etat !== "complet"))).catch(() => {});
  }, [journee.id, recapKey]);
  const closed = journee.statut === "cloturee";
  return (
    <div className="space-y-4">
      <div className="bg-card border rounded-lg">
        <div className="p-3 border-b text-xs font-bold uppercase tracking-wider">Paiements partiels, manquants et excédents ({anomalies.length})</div>
        <div className="divide-y text-xs" data-testid="cloture-anomalies">
          {anomalies.map((a) => (
            <div key={a.cle} className="p-2.5 flex flex-wrap gap-3 items-center">
              <StatusBadge value={a.etat} /><span className="font-semibold flex-1">{a.libelle}</span>
              <span>Attendu <b className="font-mono">{fmtNum(a.du)}</b></span><span>Encaissé <b className="font-mono">{fmtNum(a.encaisse)}</b></span>
              <span>Dette restante <b className={`font-mono ${a.reste > 0 ? "text-primary" : ""}`}>{fmtNum(a.reste)}</b></span>
            </div>
          ))}
          {!anomalies.length && <div className="p-3 text-muted-foreground">Tous les revendeurs ont réglé exactement le montant attendu.</div>}
        </div>
      </div>
      {closed ? (
        <div className="bg-card border rounded-lg p-4 text-sm space-y-2" data-testid="cloture-info">
          <div className="flex items-center gap-2 font-semibold"><Lock className="h-4 w-4" />Journée clôturée par {journee.closed_by_name} le {fmtDate(journee.closed_at)}</div>
          {journee.commentaire_cloture && <div className="text-muted-foreground">{journee.commentaire_cloture}</div>}
          <p className="text-xs text-muted-foreground">Les restes dus ont été enregistrés en créances, les excédents imputés sur les dettes antérieures puis en dettes, les commissions générées.</p>
          {canReopen && <Button variant="outline" onClick={onReopen} data-testid="journee-reopen-button"><Unlock className="h-4 w-4 mr-1" />Rouvrir pour correction (procédure auditée)</Button>}
        </div>
      ) : (
        <div className="bg-card border rounded-lg p-4 space-y-3">
          <p className="text-sm">À la clôture : les restes dus deviennent des créances revendeurs, les excédents soldent d'abord les créances antérieures, les commissions sont générées et la journée est verrouillée.</p>
          <Textarea placeholder="Commentaire de clôture (facultatif)" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} data-testid="cloture-commentaire" />
          {ctrl?.bloquant && <div className="text-xs text-primary">Contrôles bloquants à résoudre avant la clôture (voir l'étape 1).</div>}
          <Button disabled={!canClose || ctrl?.bloquant} onClick={() => onClose(commentaire)} data-testid="journee-close-button"><Lock className="h-4 w-4 mr-1" />Confirmer et clôturer la journée</Button>
        </div>
      )}
      {(journee.reouvertures || []).length > 0 && (
        <div className="text-xs text-muted-foreground">Réouvertures : {journee.reouvertures.map((r) => `${fmtDate(r.date)} par ${r.user_name} (${r.motif})`).join(" · ")}</div>
      )}
    </div>
  );
}

export default function JourneePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [j, setJ] = useState(null);
  const [ctrl, setCtrl] = useState(null);
  const [encs, setEncs] = useState([]);
  const [sups, setSups] = useState([]);
  const [key, setKey] = useState(0);
  const [dlg, setDlg] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, b, c, d] = await Promise.all([api.get(`/r/journees/${id}`), api.get(`/tfj/journees/${id}/controles`),
        api.get(`/tfj/journees/${id}/encaissements`), api.get(`/tfj/journees/${id}/recap`, { params: { niveau: "superviseur" } })]);
      setJ(a.data); setCtrl(b.data); setEncs(c.data); setSups(d.data.rows);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load, key]);
  const refresh = () => setKey((k) => k + 1);
  if (!j) return <div className="text-sm text-muted-foreground">Chargement…</div>;
  const open = j.statut !== "cloturee";

  const close = async (commentaire) => {
    try {
      await api.post(`/tfj/journees/${id}/cloturer`, { commentaire });
      toast.success("Journée clôturée et verrouillée");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => nav("/tfj")} data-testid="journee-back"><ArrowLeft className="h-4 w-4 mr-1" />Journées</Button>
      <PageHeader title={`Traiter la journée du ${fmtDate(j.date)}`} subtitle={`${j.nb_lignes} TPE importé(s) · ${(j.imports || []).length} import(s)`}>
        <StatusBadge value={j.statut} testId="journee-statut" />
        {open && can("tfj", "creer") && <Button size="sm" variant="outline" onClick={() => setDlg("import")} data-testid="journee-reimport"><Upload className="h-4 w-4 mr-1" />Compléter l'import</Button>}
        {open && can("tfj", "creer") && <Button size="sm" onClick={() => setDlg("enc")} data-testid="journee-add-encaissement"><Plus className="h-4 w-4 mr-1" />Encaissement</Button>}
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        {[["CA", j.total_ca], ["Gains", j.total_gains], ["Montant attendu", j.total_du], ["Encaissé", j.total_encaisse], ["Reste à encaisser", j.reste_a_encaisser]].map(([l, v], i) => (
          <div key={l} className={`p-4 rounded-lg border ${i === 4 ? "bg-[hsl(var(--sidebar))] text-white kgd-grain border-transparent" : "bg-card"}`}>
            <div className={`text-[10px] uppercase tracking-[0.18em] ${i === 4 ? "text-white/50" : "text-muted-foreground"}`}>{l}</div>
            <div className="font-mono text-lg font-bold mt-1" data-testid={`journee-kpi-${i}`}>{fmtMoney(v)}</div>
          </div>
        ))}
      </div>
      <Tabs defaultValue="controles">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="controles" data-testid="step-controles">1. Contrôles</TabsTrigger>
          <TabsTrigger value="dus" data-testid="step-dus">2. Montants dus</TabsTrigger>
          <TabsTrigger value="encaissements" data-testid="step-encaissements">3. Encaissements ({encs.filter((e) => e.statut === "valide").length})</TabsTrigger>
          <TabsTrigger value="cloture" data-testid="step-cloture">4. Clôture</TabsTrigger>
        </TabsList>
        <TabsContent value="controles"><Controles ctrl={ctrl} /></TabsContent>
        <TabsContent value="dus"><RecapTable journeeId={id} reloadKey={key} /></TabsContent>
        <TabsContent value="encaissements"><Encaissements journee={j} items={encs} canCancel={can("tfj", "annuler")} onCancel={(e) => setDlg({ cancel: e })} /></TabsContent>
        <TabsContent value="cloture">
          <Cloture journee={j} ctrl={ctrl} recapKey={key} canClose={can("tfj", "cloturer")} canReopen={can("tfj", "annuler")} onClose={close} onReopen={() => setDlg("reopen")} />
        </TabsContent>
      </Tabs>
      <EncaissementDialog open={dlg === "enc"} onOpenChange={(o) => !o && setDlg(null)} journee={j} superviseurs={sups} onDone={refresh} />
      <ImportDialog open={dlg === "import"} onOpenChange={(o) => !o && setDlg(null)} defaultDate={j.date} onDone={refresh} />
      <MotifDialog open={!!dlg?.cancel} onOpenChange={(o) => !o && setDlg(null)} title={`Annuler l'encaissement ${dlg?.cancel?.numero || ""}`}
        description="Le mouvement de trésorerie sera contre-passé." confirmLabel="Confirmer l'annulation"
        onConfirm={async (motif) => { await api.post(`/tfj/encaissements/${dlg.cancel.id}/annuler`, { motif }); toast.success("Encaissement annulé"); refresh(); }} />
      <MotifDialog open={dlg === "reopen"} onOpenChange={(o) => !o && setDlg(null)} title="Rouvrir la journée"
        description="Les créances, dettes et commissions issues de la clôture seront annulées. L'opération est tracée dans le journal d'audit."
        confirmLabel="Rouvrir" onConfirm={async (motif) => { await api.post(`/tfj/journees/${id}/rouvrir`, { motif }); toast.success("Journée rouverte"); refresh(); }} />
    </div>
  );
}
